import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  X,
  ChevronDown,
  Check,
  Search,
  Users,
  User,
  MapPin,
  Home,
  GraduationCap,
  MessageSquare,
  Send,
} from "lucide-react";

const RadioOption = ({ selected }) => (
  <span
    className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0
      ${selected ? "border-green-600 bg-green-600" : "border-gray-300"}`}
  >
    {selected && <span className="w-1.5 h-1.5 rounded-full bg-white block" />}
  </span>
);

const EditWorkerDetailModal = ({
  isOpen,
  onClose,
  token,
  workerId,
  initialData = null,
  onSaved,
  saveUrl,
  skillsUrl,
  locationsUrl,
}) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  // PUT baseURL/lenskart/update-worker-details/:workerId — workerId is a URL param, not a body field
  const SAVE_URL = saveUrl || `${BASE_URL}/lenskart/save-data`;
  const SKILLS_URL = skillsUrl || `${BASE_URL}/lenskart/skills`;
  const LOCATIONS_URL = locationsUrl || `${BASE_URL}/lenskart/locations`;

  // ── Field state ───────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedlocationobject, setselectedlocationobject] = useState(null);
  const [education, seteducation] = useState("");
  const [educationSearch, setEducationSearch] = useState("");
  const [currentAddress, setCurrentAddress] = useState("");
  const [reasonForChange, setReasonForChange] = useState("");

  // ── Dropdown open states ──────────────────────────────────────────────────
  const [isGenderOpen, setIsGenderOpen] = useState(false);
  const [isSkillsOpen, setIsSkillsOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isEducationOpen, setIsEducationOpen] = useState(false);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [skills, setSkills] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const genderRef = useRef(null);
  const skillsRef = useRef(null);
  const locationRef = useRef(null);
  const educationRef = useRef(null);

  const educationOptions = [
    "No Formal Education",
    "10th",
    "12th",
    "Graduation",
    "P.G. (Postgraduates)",
    "Diploma",
    "ITI",
    "Other",
  ];

  const filteredEducation = educationOptions.filter((opt) =>
    opt.toLowerCase().includes(educationSearch.toLowerCase())
  );

  // ── Fetch skills (prefill from initialData.skillIds) ─────────────────────
  const fetchSkills = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(SKILLS_URL);
      const data = await res.json();
      if (data.success && Array.isArray(data.skills)) {
        const active = data.skills.filter((s) => s.isActive);
        setSkills(active);
        const savedIds = initialData?.skillIds || [];
        setSelectedSkills(active.filter((s) => savedIds.includes(s._id)).slice(0, 5));
      }
    } catch {
      toast.error("Failed to load skills");
    } finally {
      setIsLoading(false);
    }
  }, [SKILLS_URL, initialData?.skillIds]);

  // ── Fetch locations (prefill from initialData.preferredLocationId) ───────
  const fetchLocations = useCallback(async () => {
    setIsLoadingLocations(true);
    try {
      const res = await fetch(LOCATIONS_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.locations)) {
        const active = data.locations.filter((loc) => loc.isActive);
        setLocations(active);
        const preferredId = initialData?.preferredLocationId;
        if (preferredId) {
          const matched = active.find((loc) => loc._id === preferredId);
          if (matched) {
            setSelectedLocation(matched.name);
            setselectedlocationobject(matched);
          }
        } else if (initialData?.preferredLocation) {
          const matched = active.find((loc) => loc.name === initialData.preferredLocation);
          if (matched) {
            setSelectedLocation(matched.name);
            setselectedlocationobject(matched);
          } else {
            setSelectedLocation(initialData.preferredLocation);
          }
        }
      }
    } catch {
      toast.error("Failed to load locations");
    } finally {
      setIsLoadingLocations(false);
    }
  }, [LOCATIONS_URL, initialData?.preferredLocationId, initialData?.preferredLocation, token]);

  // ── Prefill everything on open ───────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name || "");
      setSelectedGender(initialData?.gender || "");
      seteducation(initialData?.highestQualification || "");
      setEducationSearch("");
      // Prefill from either a prior temporaryAddress or a currentAddress field, whichever the caller supplies
      setCurrentAddress(initialData?.temporaryAddress || initialData?.currentAddress || "");
      setReasonForChange("");
      fetchSkills();
      fetchLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Close dropdowns on outside click ─────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (genderRef.current && !genderRef.current.contains(e.target))
        setIsGenderOpen(false);
      if (skillsRef.current && !skillsRef.current.contains(e.target))
        setIsSkillsOpen(false);
      if (locationRef.current && !locationRef.current.contains(e.target))
        setIsLocationOpen(false);
      if (educationRef.current && !educationRef.current.contains(e.target)) {
        setIsEducationOpen(false);
        setEducationSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Toggle single skill ───────────────────────────────────────────────────
  const toggleSkill = useCallback((skill) => {
    if (!skill) return;
    setSelectedSkills((prev) => {
      if (prev.some((s) => s._id === skill._id)) {
        return prev.filter((s) => s._id !== skill._id); // always allow deselect
      }
      if (prev.length >= 5) {
        toast.error("You can select a maximum of 5 skills");
        return prev;
      }
      return [...prev, skill];
    });
  }, []);

  const formValid =
    name.trim() &&
    selectedGender &&
    selectedSkills.length > 0 &&
    selectedLocation &&
    education.trim() &&
    currentAddress.trim() &&
    reasonForChange.trim().length >= 5;

  const handleSubmit = async () => {
    if (!formValid) return;
    setIsSubmitting(true);
    try {
      const payload = {
        name,
        workerId,
        gender: selectedGender,
        locationId: selectedlocationobject?._id,
        highestQualification: education,
        skillIds: selectedSkills.map((s) => s._id),
        temporaryAddress: currentAddress.trim(),
        remarks: reasonForChange.trim(),
        relativeName:initialData.RelativeName,
        relativeType:initialData.RelativeType,
        relativeNumber:initialData.RelativePhone,

      };

      const res = await fetch(`${SAVE_URL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (res.ok && (result.success || result.isExistingUser !== undefined)) {
        toast.success("Details updated successfully!");
        onSaved?.(payload);
        handleClose(true);
      } else {
        toast.error(result.message || "Failed to save data");
      }
    } catch {
      toast.error("Failed to save data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (success = false) => {
    setName("");
    setSelectedSkills([]);
    setSelectedLocation("");
    setSelectedGender("");
    setselectedlocationobject(null);
    seteducation("");
    setEducationSearch("");
    setCurrentAddress("");
    setReasonForChange("");
    setIsSkillsOpen(false);
    setIsLocationOpen(false);
    setIsGenderOpen(false);
    setIsEducationOpen(false);
    onClose(success);
  };

  if (!isOpen) return null;

  // ── Styles ────────────────────────────────────────────────────────────────
  const labelCls = "flex items-center gap-0.5 text-xs font-medium text-gray-700 mb-1";
  const inputWrapCls = "relative";
  const inputIconCls = "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none";
  const inputCls =
    "w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 bg-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all placeholder-gray-400";
  const dropdownBtnCls =
    "w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg bg-white text-left text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all flex justify-between items-center gap-1";
  const dropdownMenuCls =
    "absolute z-[9999] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1100] p-3">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-800">Edit Details</h2>
            
          </div>
          <button
            onClick={() => handleClose(false)}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* ── Scrollable Body ── */}
        <div className="overflow-y-auto flex-1 px-5 py-5">
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
            {/* Inner card header: icon + title, Cancel link */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-green-600 font-semibold text-sm">
                <Users className="w-4 h-4" />
                Edit Worker Details
              </div>
              <button
                type="button"
                onClick={() => handleClose(false)}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>

            <div className="space-y-4">
              {/* Name + Gender */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>
                    Name <span className="text-rose-500">*</span>
                  </label>
                  <div className={inputWrapCls}>
                    <User className={inputIconCls} />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={inputCls}
                      placeholder="Enter name"
                    />
                  </div>
                </div>

                <div className="relative" ref={genderRef}>
                  <label className={labelCls}>
                    Gender <span className="text-rose-500">*</span>
                  </label>
                  <div className={inputWrapCls}>
                    <Users className={inputIconCls} />
                    <button
                      type="button"
                      onClick={() => setIsGenderOpen((p) => !p)}
                      className={dropdownBtnCls}
                    >
                      <span className={selectedGender ? "text-gray-800" : "text-gray-400"}>
                        {selectedGender || "Select gender"}
                      </span>
                      <ChevronDown
                        className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${
                          isGenderOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>
                  {isGenderOpen && (
                    <div className={dropdownMenuCls}>
                      {["Male", "Female", "Other"].map((g) => (
                        <label
                          key={g}
                          className={`flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-green-50 ${
                            selectedGender === g ? "bg-green-50 text-green-700" : "text-gray-700"
                          }`}
                          onClick={() => {
                            setSelectedGender(g);
                            setIsGenderOpen(false);
                          }}
                        >
                          <RadioOption selected={selectedGender === g} />
                          <span className="ml-2">{g}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Location */}
              <div className="relative" ref={locationRef}>
                <label className={labelCls}>
                  Preffered Location <span className="text-rose-500">*</span>
                </label>
                <div className={inputWrapCls}>
                  <MapPin className={inputIconCls} />
                  <button
                    type="button"
                    onClick={() => setIsLocationOpen((p) => !p)}
                    className={dropdownBtnCls}
                  >
                    <span className={selectedLocation ? "text-gray-800" : "text-gray-400"}>
                      {selectedLocation || "Select location"}
                    </span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${
                        isLocationOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {isLocationOpen && (
                  <div className={dropdownMenuCls}>
                    {isLoadingLocations ? (
                      <div className="px-4 py-2 text-center text-xs text-gray-400">
                        Loading locations...
                      </div>
                    ) : locations.length > 0 ? (
                      locations.map((loc) => (
                        <label
                          key={loc._id}
                          className={`flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-green-50 ${
                            selectedLocation === loc.name
                              ? "bg-green-50 text-green-700"
                              : "text-gray-700"
                          }`}
                          onClick={() => {
                            setSelectedLocation(loc.name);
                            setselectedlocationobject(loc);
                            setIsLocationOpen(false);
                          }}
                        >
                          {selectedLocation === loc.name && (
                            <Check className="w-3 h-3 mr-2 text-green-600 flex-shrink-0" />
                          )}
                          {loc.name}
                        </label>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-center text-xs text-gray-400">
                        No locations available
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Current Address */}
              <div>
                <label className={labelCls}>
                  Current Address <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Home className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                  <textarea
  value={currentAddress}
  onChange={(e) => setCurrentAddress(e.target.value)}
  className={`${inputCls} resize-none pt-2`}
  rows={2}
  maxLength={250}
  placeholder="Enter current address"
/>
                </div>
                <div className="flex justify-end mt-0.5">
                  <p className="text-[10px] text-gray-400">{currentAddress.length}/250</p>
                </div>
              </div>

              {/* Skills */}
              <div className="relative" ref={skillsRef}>
                <label className={labelCls}>
                  Skills <span className="text-rose-500">*</span>{" "}
                  <span className="text-gray-400 font-normal">
                    {selectedSkills.length > 0 ? `(${selectedSkills.length}/5 selected)` : ""}
                  </span>
                </label>
                <div className={inputWrapCls}>
                  <GraduationCap className={inputIconCls} />
                  <button
                    type="button"
                    onClick={() => setIsSkillsOpen((p) => !p)}
                    className={`${dropdownBtnCls} min-h-[38px]`}
                  >
                    <span
                      className={`${
                        selectedSkills.length ? "flex flex-wrap gap-1" : "text-gray-400"
                      } flex-1 overflow-hidden`}
                    >
                      {selectedSkills.length
                        ? selectedSkills.map((s) => (
                            <span
                              key={s._id}
                              className="bg-green-100 text-green-700 text-[11px] font-medium px-1.5 py-0.5 rounded"
                            >
                              {s.name}
                            </span>
                          ))
                        : "Select skills"}
                    </span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${
                        isSkillsOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {isSkillsOpen && (
                  <div className={dropdownMenuCls}>
                    {isLoading ? (
                      <div className="px-4 py-2 text-center text-xs text-gray-400">
                        Loading skills...
                      </div>
                    ) : skills.length > 0 ? (
                      skills.map((skill) => (
                        <label
                          key={skill._id}
                          className={`flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-green-50 ${
                            selectedSkills.some((s) => s._id === skill._id)
                              ? "bg-green-50 text-green-700"
                              : "text-gray-700"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSkills.some((s) => s._id === skill._id)}
                            onChange={() => toggleSkill(skill)}
                            className="mr-2 accent-green-600"
                          />
                          {skill.name}
                        </label>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-center text-xs text-gray-400">
                        No skills available
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Last Education Details */}
              <div className="relative" ref={educationRef}>
                <label className={labelCls}>
                  Last Education Details <span className="text-rose-500">*</span>
                </label>
                <div className={inputWrapCls}>
                  <GraduationCap className={inputIconCls} />
                  <button
                    type="button"
                    onClick={() => {
                      setIsEducationOpen((p) => !p);
                      setEducationSearch("");
                    }}
                    className={`${dropdownBtnCls} min-h-[38px]`}
                  >
                    <span className={`truncate ${education ? "text-gray-800" : "text-gray-400"}`}>
                      {education || "Select education level"}
                    </span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${
                        isEducationOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {isEducationOpen && (
                  <div className="absolute z-[9999] mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300 overflow-hidden">
                    <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
                      <div className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 rounded-md border border-gray-200 focus-within:border-green-400">
                        <Search className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <input
                          autoFocus
                          type="text"
                          value={educationSearch}
                          onChange={(e) => setEducationSearch(e.target.value)}
                          placeholder="Search education..."
                          className="flex-1 text-xs bg-transparent outline-none text-gray-700 placeholder-gray-400"
                        />
                        {educationSearch && (
                          <button
                            onClick={() => setEducationSearch("")}
                            className="text-gray-400 hover:text-gray-600 text-xs"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="max-h-44 overflow-y-auto py-1">
                      {filteredEducation.length > 0 ? (
                        filteredEducation.map((opt) => (
                          <label
                            key={opt}
                            className={`flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-green-50 ${
                              education === opt ? "bg-green-50 text-green-700" : "text-gray-700"
                            }`}
                            onClick={() => {
                              seteducation(opt);
                              setIsEducationOpen(false);
                              setEducationSearch("");
                            }}
                          >
                            <RadioOption selected={education === opt} />
                            <span className="ml-2 truncate">{opt}</span>
                          </label>
                        ))
                      ) : (
                        <div className="px-4 py-5 text-center text-xs text-gray-400">
                          <div className="text-xl mb-1"></div>
                          No results for "{educationSearch}"
                        </div>
                      )}
                    </div>

                    <div className="px-4 py-1.5 border-t border-gray-100 bg-gray-50">
                      <p className="text-[11px] text-gray-400">
                        {filteredEducation.length} option{filteredEducation.length !== 1 ? "s" : ""} available
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Reason for change */}
              <div>
                <label className={labelCls}>
                  Reason <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                  <textarea
                    value={reasonForChange}
                    onChange={(e) => setReasonForChange(e.target.value)}
                    className={`${inputCls} resize-none pt-2`}
                    rows={3}
                    maxLength={250}
                    placeholder="Reason for updating details"
                  />
                </div>
                <div className="flex justify-between mt-0.5">
                  {reasonForChange.trim().length > 0 && reasonForChange.trim().length < 5 ? (
                    <p className="text-rose-500 text-[11px]">At least 5 characters</p>
                  ) : (
                    <span />
                  )}
                  <p className="text-[10px] text-gray-400">{reasonForChange.length}/250</p>
                </div>
              </div>

              {/* Submit */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!formValid || isSubmitting}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Update Details
              </button>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-end px-5 py-3 border-t border-gray-200 flex-shrink-0 bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={() => handleClose(false)}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditWorkerDetailModal;